import { useMemo, useState } from 'react'
import { ChartLineUp, Plus, Stack } from '@phosphor-icons/react'
import { Banner, DataTable, EmptyState, Field, Input, Select, TextArea, type Column } from '@/ui'
import { errorMessage } from '../../api/core'
import {
  STEP_CODES,
  bpText,
  stepLabel,
  useAllMachines,
  useBandHistory,
  useBands,
  useCurrentBand,
  useLoadBand,
  type BandHistoryRow,
  type BandRow,
  type MachineRow,
} from '../../api/process'
import { useCrops, useUser, type UserOption } from '../../api/reference'
import { ActionButton } from '../../components/Can'
import { FilterBar, SelectFilter } from '../../components/FilterBar'
import { FormDialog } from '../../components/FormDialog'
import { ConsolePage } from '../../components/Layout'
import { PersonPicker } from '../../components/PersonPicker'
import { Code, Kg, RefLink, Section, Status, UserName, When } from '../../components/bits'
import { bandText } from '../../model/process/format'
import { useUrlState } from '../../lib/useUrlState'
import { serverProblem } from '../../model/serverError'
import {
  draftFromBand,
  emptyYieldBandDraft,
  nextVersion,
  previousVersion,
  validateYieldBandDraft,
  yieldBandBody,
  yieldBandOk,
  type YieldBandDraft,
} from '../../model/process/yieldBandForm'

const LIMIT = 50

/**
 * Yield bands: each machine's expected product yield for a crop and step, kept as versions. A
 * change is always a new version with a note and an approver; old versions stay on record and
 * runs keep the version they were checked against.
 */
export function YieldBandsPage() {
  const [f, setF, { reset, active }] = useUrlState({ cropId: '', stepCode: '', equipmentId: '', page: 1 })
  const crops = useCrops()
  const machines = useAllMachines()
  const q = useBands({
    page: f.page,
    limit: LIMIT,
    cropId: f.cropId || undefined,
    stepCode: f.stepCode || undefined,
    equipmentId: f.equipmentId || undefined,
  })
  const [editing, setEditing] = useState<YieldBandDraft | null>(null)
  const cropName = useMemo(() => new Map((crops.data ?? []).map((c) => [c.id, c.name])), [crops.data])
  const machineById = useMemo(() => new Map((machines.data ?? []).map((m) => [m.id, m])), [machines.data])
  const lookup = f.cropId && f.stepCode ? { cropId: f.cropId, stepCode: f.stepCode, equipmentId: f.equipmentId || undefined } : null

  const columns: Column<BandRow>[] = [
    { id: 'crop', header: 'Crop', cell: (r) => <span className="font-semibold">{cropName.get(r.cropId) ?? '—'}</span> },
    { id: 'step', header: 'Step', cell: (r) => stepLabel(r.stepCode) },
    {
      id: 'machine',
      header: 'Machine',
      cell: (r) => (r.equipmentId ? <Code>{machineById.get(r.equipmentId)?.code ?? '…'}</Code> : <span className="text-muted">All machines</span>),
    },
    { id: 'version', header: 'Version', align: 'right', mono: true, cell: (r) => r.version },
    { id: 'range', header: 'Yield range', align: 'right', mono: true, cell: (r) => bandText(r) },
    { id: 'flush', header: 'Least flush', align: 'right', cell: (r) => <Kg grams={r.minFlushG} />, hideBelow: 'md' },
    { id: 'input', header: 'Least run size', align: 'right', cell: (r) => <Kg grams={r.minInputG} />, hideBelow: 'lg' },
    { id: 'from', header: 'From', mono: true, cell: (r) => r.effectiveFrom ?? '—' },
    { id: 'approved', header: 'Approved by', cell: (r) => <UserName id={r.approvedById} />, hideBelow: 'lg' },
    {
      id: 'note',
      header: 'Change note',
      cell: (r) => (r.changeNote ? <span className="text-[13px]">{r.changeNote}</span> : <span className="text-muted">{r.version === 1 ? 'First version' : '—'}</span>),
      hideBelow: 'md',
    },
    {
      id: 'actions',
      header: <span className="sr-only">Actions</span>,
      align: 'right',
      cell: (r) => (
        <span onClick={(e) => e.stopPropagation()}>
          <ActionButton perm="yield-bands:create" hide size="sm" variant="ghost" leadingIcon={<Plus />} onClick={() => setEditing(draftFromBand(r))}>
            New version
          </ActionButton>
        </span>
      ),
    },
  ]

  return (
    <ConsolePage
      eyebrow="Process"
      title="Yield bands"
      description="The product yield each machine should give for a crop and step, from its trial runs. A run outside its range, or with too much left over, is held for a decision. Ranges are never edited: a change is a new version with a note and who approved it."
      actions={
        <ActionButton
          perm="yield-bands:create"
          leadingIcon={<Plus />}
          onClick={() => setEditing(emptyYieldBandDraft({ cropId: f.cropId, stepCode: f.stepCode, equipmentId: f.equipmentId }))}
        >
          New range
        </ActionButton>
      }
    >
      {lookup ? (
        <CurrentPanel lookup={lookup} machine={f.equipmentId ? machineById.get(f.equipmentId) : undefined} onNewVersion={(b) => setEditing(draftFromBand(b))} />
      ) : (
        <p className="text-[14px] text-muted">Choose a crop and a step to see the range a new run would use and how past runs did.</p>
      )}
      <DataTable<BandRow>
        caption="Yield bands, every version"
        columns={columns}
        rows={q.data?.rows ?? []}
        getRowId={(r) => r.id}
        loading={q.isLoading}
        error={q.isError ? errorMessage(q.error) : undefined}
        pageSize={LIMIT}
        server={{ page: f.page, total: q.data?.total ?? 0, onPageChange: (page) => setF({ page }) }}
        filters={
          <FilterBar onClear={reset} active={active}>
            <SelectFilter label="Crop" value={f.cropId} onChange={(cropId) => setF({ cropId })} options={(crops.data ?? []).map((c) => ({ value: c.id, label: c.name }))} />
            <SelectFilter label="Step" value={f.stepCode} onChange={(stepCode) => setF({ stepCode })} options={STEP_CODES.map((s) => ({ value: s, label: stepLabel(s) }))} />
            <SelectFilter
              label="Machine"
              value={f.equipmentId}
              onChange={(equipmentId) => setF({ equipmentId })}
              options={(machines.data ?? []).map((m) => ({ value: m.id, label: `${m.code} · ${m.name}` }))}
            />
          </FilterBar>
        }
        empty={
          <EmptyState
            icon={<Stack />}
            title={active ? 'No ranges match' : 'No yield bands yet'}
            description="Load a machine's range from its trial runs before it processes a crop. Runs without a range are flagged."
            compact
          />
        }
      />
      {lookup && <HistoryPanel lookup={lookup} />}
      {editing && <BandDialog initial={editing} machines={machines.data ?? []} onClose={() => setEditing(null)} />}
    </ConsolePage>
  )
}

function CurrentPanel({
  lookup,
  machine,
  onNewVersion,
}: {
  lookup: { cropId: string; stepCode: string; equipmentId?: string }
  machine?: MachineRow
  onNewVersion: (b: BandRow) => void
}) {
  const cur = useCurrentBand(lookup)
  const b = cur.data
  return (
    <Section title="Range in force today" eyebrow={machine ? `${machine.code} · ${stepLabel(lookup.stepCode)}` : `${stepLabel(lookup.stepCode)} · any machine`}>
      {cur.isLoading ? (
        <p className="text-[14px] text-muted">Looking up…</p>
      ) : cur.isError ? (
        <p className="text-[14px] text-danger">{errorMessage(cur.error)}</p>
      ) : !b ? (
        <Banner tone="warn" title="No range set">
          A new run here opens without a range and is flagged at close. Load a range from the trial runs.
        </Banner>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-[14px]">
            A new run uses <span className="font-semibold">version {b.version}</span>
            {b.equipmentId ? '' : ' (all machines)'}: yield <span className="font-mono">{bandText(b)}</span>, from{' '}
            <span className="font-mono">{b.effectiveFrom ?? '—'}</span>
            {b.minFlushG !== null && (
              <>
                , least flush <Kg grams={b.minFlushG} />
              </>
            )}
            .
          </p>
          <ActionButton perm="yield-bands:create" hide size="sm" variant="secondary" leadingIcon={<Plus />} onClick={() => onNewVersion(b)}>
            New version
          </ActionButton>
        </div>
      )}
    </Section>
  )
}

function HistoryPanel({ lookup }: { lookup: { cropId: string; stepCode: string; equipmentId?: string } }) {
  const h = useBandHistory({ ...lookup, limit: 50 })
  const columns: Column<BandHistoryRow>[] = [
    { id: 'run', header: 'Run', cell: (r) => <RefLink to={`/c/runs/${r.runId}`}>{r.runCode}</RefLink> },
    { id: 'ended', header: 'Ended', cell: (r) => <When at={r.endedAt} dateOnly />, hideBelow: 'md' },
    { id: 'in', header: 'In', align: 'right', cell: (r) => <Kg grams={r.inputG} /> },
    {
      id: 'yield',
      header: 'Yield',
      align: 'right',
      cell: (r) => {
        const v =
          r.yieldBp === null || r.minBp === null || r.maxBp === null
            ? null
            : r.yieldBp >= r.minBp && r.yieldBp <= r.maxBp
        return <span className={v === false ? 'font-mono font-semibold text-danger' : v ? 'font-mono text-ok' : 'font-mono'}>{bpText(r.yieldBp)}</span>
      },
    },
    { id: 'range', header: 'Range used', align: 'right', mono: true, cell: (r) => (r.bandVersion ? `${bandText(r)} · v${r.bandVersion}` : 'No range') },
    {
      id: 'left',
      header: 'Left over',
      align: 'right',
      cell: (r) => {
        const over = r.residualBp !== null && r.lossLimitBp !== null && Math.abs(r.residualBp) > r.lossLimitBp
        return <span className={over ? 'font-mono font-semibold text-danger' : 'font-mono'}>{bpText(r.residualBp)}</span>
      },
    },
    { id: 'status', header: 'Status', cell: (r) => <Status status={r.status} /> },
  ]
  const rows = h.data ?? []
  const inBand = rows.filter((r) => r.yieldBp !== null && r.minBp !== null && r.maxBp !== null && r.yieldBp >= r.minBp && r.yieldBp <= r.maxBp).length
  return (
    <Section title="How past runs did" eyebrow={rows.length ? `${inBand} of ${rows.length} inside their range` : undefined} bodyClassName="p-0">
      <DataTable<BandHistoryRow>
        caption="Past runs' yield and left over"
        columns={columns}
        rows={rows}
        getRowId={(r) => r.runId}
        loading={h.isLoading}
        error={h.isError ? errorMessage(h.error) : undefined}
        pageSize={50}
        empty={<EmptyState icon={<ChartLineUp />} title="No closed runs yet" description="Runs of this crop and step appear here once they close." compact />}
      />
    </Section>
  )
}

function BandDialog({ initial, machines, onClose }: { initial: YieldBandDraft; machines: MachineRow[]; onClose: () => void }) {
  const [d, setD] = useState<YieldBandDraft>(initial)
  const [touched, setTouched] = useState(false)
  const crops = useCrops()
  const approver = useUser(d.approvedById || null)
  const existing = useBands({ cropId: d.cropId || undefined, stepCode: d.stepCode || undefined, page: 1, limit: 200 }, Boolean(d.cropId && d.stepCode))
  const prev = d.cropId && d.stepCode ? previousVersion(existing.data?.rows ?? [], d) : null
  const save = useLoadBand(() => true)
  const p = validateYieldBandDraft(d, prev, STEP_CODES)
  const shown = (k: keyof YieldBandDraft) => (touched ? p[k] : undefined)
  const version = nextVersion(prev)
  const err = save.isError ? serverProblem(save.error) : null
  const stepMachines = machines.filter((m) => !d.stepCode || m.stepCodes.includes(d.stepCode))
  const approverValue: UserOption | null = d.approvedById
    ? (approver.data ?? { id: d.approvedById, name: '…', username: '', farmerCode: null, contactNo: null, roles: [] })
    : null

  return (
    <FormDialog
      open
      onClose={onClose}
      title={version > 1 ? `New range version · version ${version}` : 'New range'}
      description="Figures from the machine's trial runs. Saved as a new version; earlier versions are kept as they are."
      saveLabel={`Save version ${version}`}
      loading={save.isPending}
      canSave={!touched || yieldBandOk(p)}
      onSave={() => {
        setTouched(true)
        if (!yieldBandOk(p) || existing.isLoading) return Promise.reject(new Error('invalid'))
        return save.mutateAsync(yieldBandBody(d))
      }}
    >
      {err && (
        <Banner tone="error" title={err.message}>
          {err.details.join(' · ')}
        </Banner>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Field label="Crop" required error={shown('cropId')}>
          <Select value={d.cropId} onChange={(cropId) => setD({ ...d, cropId })} placeholder="Choose…" options={(crops.data ?? []).map((c) => ({ value: c.id, label: c.name }))} />
        </Field>
        <Field label="Step" required error={shown('stepCode')}>
          <Select value={d.stepCode} onChange={(stepCode) => setD({ ...d, stepCode })} placeholder="Choose…" options={STEP_CODES.map((s) => ({ value: s, label: stepLabel(s) }))} />
        </Field>
        <Field label="Machine" hint="Empty: every machine of the step.">
          <Select
            value={d.equipmentId}
            onChange={(equipmentId) => setD({ ...d, equipmentId })}
            options={[{ value: '', label: 'All machines' }, ...stepMachines.map((m) => ({ value: m.id, label: `${m.code} · ${m.name}` }))]}
          />
        </Field>
      </div>
      {prev && (
        <p className="rounded-card border border-rule bg-paper-tint/40 px-3 py-2 text-[13.5px]">
          Version {prev.version} is in force from <span className="font-mono">{prev.effectiveFrom ?? '—'}</span>. This saves version {version}; the new date cannot be before that.
        </p>
      )}
      <div className="grid grid-cols-2 gap-4">
        <Field label="Lowest yield" required error={shown('minPct')}>
          <Input inputMode="decimal" value={d.minPct} onChange={(e) => setD({ ...d, minPct: e.target.value })} trailing="%" className="font-mono" placeholder="68" />
        </Field>
        <Field label="Highest yield" required error={shown('maxPct')}>
          <Input inputMode="decimal" value={d.maxPct} onChange={(e) => setD({ ...d, maxPct: e.target.value })} trailing="%" className="font-mono" placeholder="74" />
        </Field>
        <Field label="Least flush" error={shown('minFlushKg')} hint="What stays in the machine; the first kilos of a run are kept apart.">
          <Input inputMode="decimal" value={d.minFlushKg} onChange={(e) => setD({ ...d, minFlushKg: e.target.value })} trailing="kg" className="font-mono" />
        </Field>
        <Field label="Least run size" error={shown('minInputKg')}>
          <Input inputMode="decimal" value={d.minInputKg} onChange={(e) => setD({ ...d, minInputKg: e.target.value })} trailing="kg" className="font-mono" />
        </Field>
        <Field label="In force from" error={shown('effectiveFrom')} hint="Empty: today.">
          <Input type="date" value={d.effectiveFrom} onChange={(e) => setD({ ...d, effectiveFrom: e.target.value })} />
        </Field>
        <Field label="Figures from">
          <Select<'MANUAL' | 'LEARNED'>
            value={d.source}
            onChange={(source) => setD({ ...d, source })}
            options={[
              { value: 'MANUAL', label: 'Trial runs' },
              { value: 'LEARNED', label: 'Past runs' },
            ]}
          />
        </Field>
      </div>
      <Field label="Change note" required={version > 1} error={shown('changeNote')} hint="What changed and why, e.g. new drum fitted, trial runs on 3 lots.">
        <TextArea rows={2} value={d.changeNote} onChange={(e) => setD({ ...d, changeNote: e.target.value })} maxLength={200} />
      </Field>
      <Field label="Approved by" required={version > 1} error={shown('approvedById')} hint={version > 1 ? undefined : 'Empty: you.'}>
        <PersonPicker label="Approved by" value={approverValue} onChange={(u) => setD({ ...d, approvedById: u?.id ?? '' })} invalid={Boolean(shown('approvedById'))} />
      </Field>
    </FormDialog>
  )
}
